import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, ReplaySubject, switchMap, throwError } from 'rxjs';
import { DocumentType } from 'src/app/compliance/models/document-type';
import { DocumentTypeSummaryBase } from 'src/app/shared/lib/compliance-shared/models/document-type-summary-base.model';
import { FetchArgs, PagingTokenFixed } from 'src/app/shared/models/core';
import { LineOfBusiness } from 'src/app/shared/models/enums/line-of-business';
import { KeyValue } from 'src/app/shared/models/key-value.model';
import { FeatureFlag } from '../../../shared/models/enums/feature-flag';
import { CategoryLineOfBusiness } from '../../enums/category-line-of-business.enum';
import { EsignTemplate } from '../../models/doc-admin-esign-template.model';
import { DocAdminPermissions } from '../../models/doc-admin-permissions.model';
import { DocumentTypeCategory } from '../../models/document-type-category.model';
import { DocumentTypesCreate } from '../../models/document-types-create.model';
import { DocumentTypesUpdate } from '../../models/document-types-update.model';
import { DocmentTypeFile, DocumentTypes } from '../../models/document-types.model';
import { ProfessionTree } from '../../models/profession-tree.model';
import { DocAdminService } from '../../requirements-admin/doc-admin.service';
import { DocumentAdminClient } from '../document-admin-client.service';
import { DocumentTypesClient } from '../document-types-client.service';
import { CategoryLineOfBusinessEnum } from '../enums/category-line-of-business-enum.enum';
import { LineOfBusinessEnum } from '../enums/line-of-business-enum.enum';
import { AddDocumentTypeCodeRequest } from '../models/add-document-type-code-request.model';
import { AddDocumentTypeRequest } from '../models/add-document-type-request.model';
import { AdminDocumentDto } from '../models/admin-document-dto.model';
import { DocumentAdminSummaryFilterDto } from '../models/document-admin-summary-filter-dto.model';
import { DocumentTypeCategoryDto } from '../models/document-type-category-dto.model';
import { DocumentTypeCodeDto } from '../models/document-type-code-dto.model';
import { DocumentTypeCodeSetDto } from '../models/document-type-code-set-dto.model';
import { DocumentTypeCreateDto } from '../models/document-type-create-dto.model';
import { DocumentTypeDetailDto } from '../models/document-type-detail-dto.model';
import { DocumentTypeUpdateDto } from '../models/document-type-update-dto.model';
import { KeyValueDto } from '../models/key-value-dto.model';
import { PagingTokenOfDocumentAdminSummaryDto } from '../models/paging-token-of-document-admin-summary-dto.model';
import { ProfessionTreeDto } from '../models/profession-tree-dto.model';
import { QmAssociationDto } from '../models/qm-association-dto.model';
import { UpdateDocumentTypeCodeRequest } from '../models/update-document-type-code-request.model';
import { UpdateDocumentTypeRequest } from '../models/update-document-type-request.model';
import { gridDate } from './grid-date.function';
import { NSwagMigrationService } from './nswag-migration.service';
import { WorkTypeSettingsDto } from '../models/work-type-settings-dto.model';
import { LocumsContractTypeSettingsDto } from '../models/locums-contract-type-settings-dto.model';
import { ManualReviewCandidateTypeDto } from '../models/manual-review-candidate-type-dto.model';

// 31 endpoints
@Injectable({ providedIn: 'root' })
export class DocAdminNSwagMigrationService extends NSwagMigrationService {
    protected readonly className = 'DocAdminNSwagMigrationService';
    protected readonly featureFlag = FeatureFlag.NSwagMigrationDocAdminService;
    private readonly legacy = inject(DocAdminService);
    private readonly documentAdminClient = inject(DocumentAdminClient);
    private readonly documentTypesClient = inject(DocumentTypesClient);

    /** TODO - This needs to be moved into a separate service.  Does not belong with the HTTP stuff. */
    get isNoStatesOrSpecialties$(): Observable<boolean> {
        return this.legacy.isNoStatesOrSpecialties$;
    }
    set isNoStatesOrSpecialties$(value: Observable<boolean>) {
        this.legacy.isNoStatesOrSpecialties$ = value;
    }
    get isNoStatesOrSpecialtiesSnapshot$(): Observable<boolean> {
        return this.legacy.isNoStatesOrSpecialtiesSnapshot$;
    }
    set isNoStatesOrSpecialtiesSnapshot$(value: Observable<boolean>) {
        this.legacy.isNoStatesOrSpecialtiesSnapshot$ = value;
    }
    get CurrentStates$(): ReplaySubject<string[]> {
        return this.legacy.CurrentStates$;
    }
    get CurrentSnapshotStates$(): ReplaySubject<string[]> {
        return this.legacy.CurrentSnapshotStates$;
    }
    get CurrentProfessionAndSpecialtySettings$(): ReplaySubject<string[]> {
        return this.legacy.CurrentProfessionAndSpecialtySettings$;
    }
    get SnapshotProfessionAndSpecialtySettings$(): ReplaySubject<string[]> {
        return this.legacy.SnapshotProfessionAndSpecialtySettings$;
    }
    get CurrentFacilityLinesOfBusiness$(): ReplaySubject<KeyValue<number, KeyValue<string, boolean>>> {
        return this.legacy.CurrentFacilityLinesOfBusiness$;
    }
    get CurrentSnapshotFacilityLinesOfBusiness$(): ReplaySubject<KeyValue<number, KeyValue<string, boolean>>> {
        return this.legacy.CurrentSnapshotFacilityLinesOfBusiness$;
    }
    get CurrentSampleFile$(): ReplaySubject<File> {
        return this.legacy.CurrentSampleFile$;
    }
    get CurrentSampleBlob$(): ReplaySubject<Blob> {
        return this.legacy.CurrentSampleBlob$;
    }
    get ExistingDocuSignTemplates$(): BehaviorSubject<EsignTemplate[]> {
        return this.legacy.ExistingDocuSignTemplates$;
    }
    get SelectedDocuSignTemplates$(): BehaviorSubject<EsignTemplate[]> {
        return this.legacy.SelectedDocuSignTemplates$;
    }
    get SnapshotUpdated$(): BehaviorSubject<boolean> {
        return this.legacy.SnapshotUpdated$;
    }
    get CurrentExternalInstructions$(): BehaviorSubject<string> {
        return this.legacy.CurrentExternalInstructions$;
    }

    get AcceptanceCriteriaTypeOptions(): KeyValue<number, string>[] {
        return this.legacy.AcceptanceCriteriaTypeOptions;
    }
    get AcceptanceCriteriaStatusOptions(): KeyValue<number, string>[] {
        return this.legacy.AcceptanceCriteriaStatusOptions;
    }
    setCurrentStates(value: string[]): void {
        this.legacy.setCurrentStates(value);
    }
    setCurrentSnapshotStates(value: string[]): void {
        this.legacy.setCurrentSnapshotStates(value);
    }
    setCurrentProfessionAndSpecialtySettings(value: string[]): void {
        this.legacy.setCurrentProfessionAndSpecialtySettings(value);
    }
    setCurrentSnapShotProfessionAndSpecialtySettings(value: string[]): void {
        this.legacy.setCurrentSnapShotProfessionAndSpecialtySettings(value);
    }
    setCurrentFacilityLinesOfBusiness(value: KeyValue<number, KeyValue<string, boolean>>): void {
        this.legacy.setCurrentFacilityLinesOfBusiness(value);
    }
    setCurrentSnapshotFacilityLinesOfBusiness(value: KeyValue<number, KeyValue<string, boolean>>): void {
        this.legacy.setCurrentSnapshotFacilityLinesOfBusiness(value);
    }
    setCurrentSampleFile(file: File): void {
        this.legacy.setCurrentSampleFile(file);
    }
    setCurrentSampleBlob(blob: Blob): void {
        this.legacy.setCurrentSampleBlob(blob);
    }
    setExistingDocuSignTemplates(templates: EsignTemplate[]): void {
        this.legacy.setExistingDocuSignTemplates(templates);
    }
    setSelectedDocuSignTemplates(templates: EsignTemplate[]): void {
        this.legacy.setSelectedDocuSignTemplates(templates);
    }
    setSnapshotUpdated(isUpdated: boolean): void {
        this.legacy.setSnapshotUpdated(isUpdated);
    }
    setCurrentExternalInstructions(externalInstructions: string): void {
        this.legacy.setCurrentExternalInstructions(externalInstructions);
    }

    /******************************************************************************************************/

    // ** Does not exist.
    getPermissions(): Observable<DocAdminPermissions> {
        return this.invoke((x) =>
            x ? this.legacy.getPermissions() : throwError(() => new Error('This endpoint does not exist!'))
        );
    }

    // ** DocumentAdminController.GetFacilityLookups
    getFacilityLookups(name: string, categoryLob: number): Observable<KeyValue<number, string>[] | KeyValueDto[]> {
        return this.invoke((x) =>
            x
                ? this.legacy.getFacilityLookups(name, categoryLob)
                : this.documentAdminClient.getFacilityLookups(categoryLob, name)
        );
    }

    // ** DocumentAdminController.GetAssociationLookups
    getAssociationLookups(name: string): Observable<KeyValue<number, string>[] | QmAssociationDto[]> {
        return this.invoke((x) =>
            x ? this.legacy.getAssociationLookups(name) : this.documentAdminClient.getAssociationLookups(name)
        );
    }

    // ** DocumentAdminController.UploadDocumentFile
    uploadDocumentFile(
        documentFile: DocmentTypeFile,
        documentId: number
    ): Observable<DocumentTypes | AdminDocumentDto> {
        const file = documentFile.document;
        return this.invoke((x) =>
            x
                ? this.legacy.uploadDocumentFile(documentFile, documentId)
                : this.documentAdminClient.uploadDocumentFile(documentId, { data: file, fileName: file.name })
        );
    }

    // ** DocumentAdminController.UploadDocument
    uploadDocument(
        document: DocumentTypesCreate | DocumentTypeCreateDto
    ): Observable<DocumentTypes | AdminDocumentDto> {
        return this.invoke((x) =>
            x
                ? this.legacy.uploadDocument(document as DocumentTypesCreate)
                : this.documentAdminClient.uploadDocument(document as DocumentTypeCreateDto)
        );
    }

    // ** DocumentAdminController.UpdateDocument
    updateDocument(
        document: DocumentTypesUpdate | DocumentTypeUpdateDto
    ): Observable<DocumentTypes | AdminDocumentDto> {
        return this.invoke((x) =>
            x
                ? this.legacy.updateDocument(document as DocumentTypesUpdate)
                : this.documentAdminClient.updateDocument(document as DocumentTypeUpdateDto)
        );
    }

    disableDocument(
        document: DocumentTypesUpdate | DocumentTypeUpdateDto
    ): Observable<DocumentTypes | AdminDocumentDto> {
        return this.documentAdminClient.disableRequirement(document as DocumentTypeUpdateDto);
    }

    enableDocument(
        document: DocumentTypesUpdate | DocumentTypeUpdateDto
    ): Observable<DocumentTypes | AdminDocumentDto> {
        return this.documentAdminClient.enableRequirement(document as DocumentTypeUpdateDto);
    }

    // ** DocumentAdminController.GetCandidateTaskTypes
    getCandidateTaskTypes(includeDefault?: boolean): Observable<KeyValue<number, string>[] | KeyValueDto[]> {
        return this.invoke((x) =>
            x
                ? this.legacy.getCandidateTaskTypes(includeDefault)
                : this.documentAdminClient.getCandidateTaskTypes(includeDefault)
        );
    }

    // ** DocumentAdminController.GetPartnerNetworkTaskTypes
    getPartnerNetworkTaskTypes(): Observable<KeyValue<number, string>[] | KeyValueDto[]> {
        return this.invoke((x) =>
            x ? this.legacy.getPartnerNetworkTaskTypes() : this.documentAdminClient.getPartnerNetworkTaskTypes()
        );
    }

    // ** DocumentAdminController.GetSpecialties
    getSpecialties(getLocums: boolean): Observable<KeyValue<number, string>[] | KeyValueDto[]> {
        return this.invoke((x) =>
            x ? this.legacy.getSpecialties(getLocums) : this.documentAdminClient.getSpecialties(getLocums)
        );
    }

    // ** DocumentAdminController.GetStates
    getStates(): Observable<KeyValue<number, string>[] | KeyValueDto[]> {
        return this.invoke((x) => (x ? this.legacy.getStates() : this.documentAdminClient.getStates()));
    }

    // ** DocumentAdminController.GetFacilityTypes
    getFacilityTypes(): Observable<KeyValue<number, string>[] | KeyValueDto[]> {
        return this.documentAdminClient.getFacilityTypes();
    }    

    // ** DocumentAdminController.GetManualReviewTypes
    getManualReviewCandidateTypes(): Observable<ManualReviewCandidateTypeDto[]> {
        return this.documentAdminClient.getManualReviewCandidateTypes();
    }

    // ** DocumentAdminController.GetWorkTypeSettings
    getWorkTypeSettings(): Observable<WorkTypeSettingsDto[]> {
        return this.documentAdminClient.getWorkTypeSettings();
    }

    // ** DocumentAdminController.GetLocumsContractTypeSettings
    getLocumsContractTypeSettings(): Observable<LocumsContractTypeSettingsDto[]> {
        return this.documentAdminClient.getLocumsContractTypeSettings();
    }
    // ** DocumentAdminController.GetRequirementLevels
    getRequirementLevels(): Observable<KeyValue<number, string>[] | KeyValueDto[]> {
        return this.invoke((x) =>
            x ? this.legacy.getRequirementLevels() : this.documentAdminClient.getRequirementLevels()
        );
    }

    // ** DocumentTypesController.GetAllDocumentTypeCategories or DocumentTypesController.GetDocumentTypeCategoriesByLobAsync
    getDocumentTypesCategories(
        clob: CategoryLineOfBusiness | CategoryLineOfBusinessEnum
    ): Observable<DocumentTypeCategory[] | string[] | DocumentTypeCategoryDto[]> {
        const { documentTypesClient, featureManager, legacy } = this;

        return this.invoke((x) => {
            if (x) {
                return legacy.getDocumentTypesCategories(clob as CategoryLineOfBusiness);
            }

            if (clob === 0) {
                return documentTypesClient.getAllDocumentTypeCategories();
            }

            return  documentTypesClient.getDocumentTypeCategoriesByCLob(clob as CategoryLineOfBusinessEnum);        
        });
    }

    // ** DocumentAdminController.GetDocumentAdmin
    getDocumentTypes(
        args: FetchArgs
    ): Observable<PagingTokenFixed<DocumentTypeSummaryBase[]> | PagingTokenOfDocumentAdminSummaryDto> {
        const filter: DocumentAdminSummaryFilterDto = args.filter ?? {};
        return this.invoke((x) =>
            x
                ? this.legacy.getDocumentTypes(args)
                : this.documentAdminClient.getDocumentAdmin(
                      filter.id, // filter_Id: number | null | undefined,
                      filter.facilityId, // filter_FacilityId: number | null | undefined
                      filter.facilityName, // filter_FacilityName: string | null | undefined
                      filter.associationId, // filter_AssociationId: number | null | undefined
                      filter.associationName, // filter_AssociationName: string | null | undefined
                      filter.stateId, // filter_StateId: number | null | undefined
                      filter.stateName, // filter_StateName: string | null | undefined
                      filter.name, // filter_Name: string | null | undefined
                      filter.candidateTaskTypeName, // filter_CandidateTaskTypeName: string | null | undefined
                      filter.candidateTaskTypeId, // filter_CandidateTaskTypeId: number[] | null | undefined
                      filter.qmDefault, // filter_QmDefault: boolean | null | undefined
                      filter.requiresExpiration, // filter_RequiresExpiration: boolean | null | undefined
                      filter.category, // filter_Category: string[] | null | undefined
                      gridDate(filter.lastModified), // filter_LastModified: Date | null | undefined
                      filter.linesOfBusiness, // filter_LinesOfBusiness: number[] | null | undefined
                      filter.categoryLineOfBusinessId, // filter_CategoryLineOfBusinessId: number[] | null | undefined
                      filter.requirementLevel, // filter_RequirementLevel: DocumentTypeLevelType | undefined
                      filter.acceptanceCriteriaType, // filter_AcceptanceCriteriaType: AcceptanceCriteriaType2[] | null | undefined
                      args.pageSize,
                      args.skip,
                      args.sort?.field,
                      args.sort?.dir as any,
                      undefined
                  )
        );
    }

    // ** DocumentAdminController.IsOcrEnabled
    getEnableOcrStatus(): Observable<boolean> {
        return this.invoke((x) => (x ? this.legacy.getEnableOcrStatus() : this.documentAdminClient.isOcrEnabled()));
    }

    // ** DocumentAdminController.ToggleOcrEnabled
    toggleOcrStatus(): Observable<any | void> {
        return this.invoke((x) => (x ? this.legacy.toggleOcrStatus() : this.documentAdminClient.toggleOcrEnabled()));
    }

    // ** DocumentAdminController.DownloadFile
    downloadDocumentBlobById(documentId: number): Observable<Blob> {
        return this.invoke((x) =>
            x
                ? this.legacy.downloadDocumentBlobById(documentId)
                : this.documentAdminClient.downloadFile(documentId).pipe(map((x) => x?.data))
        );
    }

    // ** DocumentAdminController.GetDocumentTypeAsync
    getFacilitySpecificDocument(id: number): Observable<DocumentTypes | AdminDocumentDto> {
        return this.invoke((x) =>
            x ? this.legacy.getFacilitySpecificDocument(id) : this.documentAdminClient.getDocumentType(id)
        );
    }

    // ** DocumentAdminController.InitializeDocumentTypeAsync
    initializeDocument(): Observable<DocumentTypes | AdminDocumentDto> {
        return this.invoke((x) =>
            x ? this.legacy.initializeDocument() : this.documentAdminClient.initializeDocumentType()
        );
    }

    // ** DocumentAdminController.DownloadFile
    downloadExampleDocument(documentId: number): Observable<Blob> {
        return this.invoke((x) =>
            x
                ? this.legacy.downloadExampleDocument(documentId)
                : this.documentAdminClient.downloadFile(documentId).pipe(map((x) => x?.data))
        );
    }

    // ** DocumentAdminController.CreatePdf
    createNewDocument(id: number, pages: number[]): Observable<DocumentTypes | AdminDocumentDto> {
        return this.invoke((x) =>
            x ? this.legacy.createNewDocument(id, pages) : this.documentAdminClient.createPdf(id, pages)
        );
    }

    // ** DocumentAdminController.RemoveRequirement
    removeRequirement(id: number): Observable<DocumentTypes | AdminDocumentDto> {
        return this.invoke((x) =>
            x ? this.legacy.removeRequirement(id) : this.documentAdminClient.removeRequirement(id)
        );
    }

    // ** Does not exist.
    getDocumentTypesList(name: string = null): Observable<DocumentType[]> {
        return this.invoke((x) =>
            x ? this.legacy.getDocumentTypesList(name) : throwError(() => new Error('This endpoint does not exist!'))
        );
    }

    // ** DocumentTypesController.CreateDocumentTypeAsync
    createDocumentType(
        docType: DocumentType | AddDocumentTypeRequest
    ): Observable<DocumentType | DocumentTypeDetailDto> {
        return this.invoke((x) =>
            x
                ? this.legacy.createDocumentType(docType as DocumentType)
                : this.documentTypesClient.createDocumentType(docType as AddDocumentTypeRequest)
        );
    }

    // ** DocumentTypesController.AddDocumentTypeCodeSetAsync
    addSubType(name: string, id: number): Observable<unknown | DocumentTypeCodeSetDto> {
        return this.invoke((x) =>
            x ? this.legacy.addSubType(name, id) : this.documentTypesClient.addDocumentTypeCodeSet(id, { name })
        );
    }

    // ** DocumentTypesController.AddDocumentTypeCodesAsync
    addDocCode(
        newDocCode: unknown | AddDocumentTypeCodeRequest,
        id: number
    ): Observable<unknown | DocumentTypeCodeDto> {
        return this.invoke((x) =>
            x
                ? this.legacy.addDocCode(newDocCode, id)
                : this.documentTypesClient.addDocumentTypeCodes(id, newDocCode as AddDocumentTypeCodeRequest)
        );
    }

    // ** DocumentTypesController.UpdateDocumentTypeCodeAsync
    editDocCode(
        newDocCode: unknown | UpdateDocumentTypeCodeRequest,
        id: number,
        codeId: number
    ): Observable<unknown | DocumentTypeCodeDto> {
        return this.invoke((x) =>
            x
                ? this.legacy.editDocCode(newDocCode, id, codeId)
                : this.documentTypesClient.updateDocumentTypeCode(
                      id,
                      codeId,
                      newDocCode as UpdateDocumentTypeCodeRequest
                  )
        );
    }

    // ** DocumentTypesController.RemoveDocumentTypeCodeAsync
    deleteDocCode(id: number, codeId: number): Observable<unknown | void> {
        return this.invoke((x) =>
            x ? this.legacy.deleteDocCode(id, codeId) : this.documentTypesClient.removeDocumentTypeCode(id, codeId)
        );
    }

    // ** DocumentTypesController.UpdateDocumentTypeAsync
    updateDocumentType(
        docType: DocumentType | UpdateDocumentTypeRequest,
        id: number
    ): Observable<DocumentType | DocumentTypeDetailDto> {
        return this.invoke((x) =>
            x
                ? this.legacy.updateDocumentType(docType as DocumentType, id)
                : this.documentTypesClient.updateDocumentType(id, docType as UpdateDocumentTypeRequest)
        );
    }

    // ** DocumentTypesController.GetDocumentTypeByIdAsync
    getDocumentType(id: number): Observable<DocumentType | DocumentTypeDetailDto> {
        return this.invoke((x) =>
            x ? this.legacy.getDocumentType(id) : this.documentTypesClient.getDocumentTypeById(id)
        );
    }

    // ** Does not exist.
    uploadSupplementalFile(file: File): Observable<unknown> {
        return this.invoke((x) =>
            x ? this.legacy.uploadSupplementalFile(file) : throwError(() => new Error('This endpoint does not exist!'))
        );
    }

    // ** DocumentAdminController.GetProfessionTreeAsync
    getProfessionTree(): Observable<ProfessionTree | ProfessionTreeDto> {
        return this.invoke((x) => (x ? this.legacy.getProfessionTree() : this.documentAdminClient.getProfessionTree()));
    }
}
